import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';

// API Response Interface
export interface ExamApiResponse {
  id: string;
  examTitle: string;
  valuerRank: string;
  examDate: string | null;
  examTime: string;
  examLocation: string;
  examFee: string;
  registrationDeadline: string | null;
  remarks: string;
  posterFile: string | null;
  certificateFile: string | null;
  examStatusId: number;
  createdAt: string;
  updatedAt: string;
  examStatus: {
    id: number;
    name: string;
  };
  cpdHours: Array<{
    id: string;
    cpdHoursId: number;
    cpdHours: {
      id: number;
      name: string;
    };
  }>;
  userExams?: Array<{
    id: string;
    enrolledAt: string;
    completedAt: string | null;
    progress: string;
    isCompleted: boolean;
    totalHoursStudied: string;
    notes: string;
    createdAt: string;
    updatedAt: string;
  }>;
}

export interface ExamDetail {
  id?: string;
  examTopic: string;
  valuerRank: string;
  examDate: string | null;
  examTime: string;
  examLocation: string;
  examFee: number;
  registrationDeadline: string | null;
  remarks?: string;
  posterImageUrl?: string;
  certificateFile?: string | null;
  examStatus: string;
  examStatusId?: number;
  cpdHoursDisplay: string;
  enrolledCount?: number;
  rating?: number;
  reviewCount?: number;
  whatYouWillLearn?: string[];
  whoShouldAttend?: string[];
  examDescription?: string;
  examObjectives?: string[];
}

@Component({
  selector: 'app-user-exam-register',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './user-exam-register.component.html',
  styleUrl: './user-exam-register.component.scss'
})
export class UserExamRegisterComponent implements OnInit {
  examId: string | null = null;
  examData: ExamDetail | null = null;
  isLoading = false;
  activeTab: string = 'overview';
  
  // File upload properties
  selectedFile: File | null = null;
  filePreviewUrl: string | null = null;
  fileError: string | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    console.log('🚀 UserExamRegister: Component initialized');

    // Subscribe to route params to handle navigation changes
    this.route.paramMap.subscribe(params => {
      this.examId = params.get('id');
      console.log('📋 Exam ID from route:', this.examId);

      if (this.examId) {
        this.loadExamData(this.examId);
      } else {
        console.error('❌ No exam ID provided');
        alert('ไม่พบรหัสการสอบ');
        this.goBack();
      }
    });
  }

  /**
   * Load exam data from API
   */
  private loadExamData(examId: string): void {
    this.isLoading = true;
    const apiUrl = `${environment.apiUrl}/exams/${examId}`;
    console.log('📡 Fetching exam data from:', apiUrl);

    this.http.get<ExamApiResponse>(apiUrl, {
      observe: 'response' // Get full response to handle 304
    }).subscribe({
      next: (response) => {
        console.log('✅ API Response Status:', response.status);
        console.log('✅ API Response Data:', response.body);

        // Handle both 200 and 304 status codes
        if (response.body) {
          this.examData = this.mapApiResponseToExamDetail(response.body);
          console.log('✅ Exam data mapped:', this.examData);
        } else {
          console.warn('⚠️ Response body is null (304 Not Modified)');
        }

        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error loading exam data:', error);
        console.error('❌ Error status:', error.status);
        console.error('❌ Error message:', error.message);

        this.isLoading = false;

        if (error.status === 404) {
          alert('ไม่พบการสอบที่ต้องการ');
        } else if (error.status === 0) {
          alert('ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบว่า Backend ทำงานอยู่');
        } else {
          alert('ไม่สามารถโหลดข้อมูลการสอบได้ กรุณาลองใหม่อีกครั้ง');
        }

        this.goBack();
      }
    });
  }

  /**
   * Map API response to ExamDetail interface
   */
  private mapApiResponseToExamDetail(apiData: ExamApiResponse): ExamDetail {
    console.log('🔄 Mapping API data to ExamDetail...');
    console.log('📊 API Data:', apiData);

    const enrolledCount = apiData.userExams?.length || 0;

    // Extract CPD hours names from the cpdHours array
    const cpdHoursNames = apiData.cpdHours?.map(item => item.cpdHours.name).join(', ') || '0 ชั่วโมง';

    return {
      id: apiData.id,
      examTopic: apiData.examTitle,
      valuerRank: apiData.valuerRank,
      examDate: apiData.examDate,
      examTime: apiData.examTime,
      examLocation: apiData.examLocation,
      examFee: parseFloat(apiData.examFee) || 0,
      registrationDeadline: apiData.registrationDeadline,
      remarks: apiData.remarks,
      posterImageUrl: apiData.posterFile || undefined,
      certificateFile: apiData.certificateFile,
      examStatus: apiData.examStatus?.name || 'เปิดรับสมัคร',
      examStatusId: apiData.examStatusId,
      cpdHoursDisplay: cpdHoursNames,
      enrolledCount: enrolledCount,
      rating: 4.5 + Math.random() * 0.5,
      reviewCount: Math.floor(Math.random() * 100) + 50,
      whatYouWillLearn: [
        `เตรียมความพร้อมสำหรับการสอบ${apiData.examTitle}`,
        `พัฒนาทักษะการสอบในระดับ ${apiData.valuerRank}`,
        `ฝึกทำข้อสอบจริงกับกรณีศึกษาที่หลากหลาย`,
        `เข้าใจมาตรฐานและข้อกำหนดทางวิชาชีพ`
      ],
      examDescription: apiData.remarks,
      examObjectives: [
        `เข้าใจหลักการพื้นฐานของการสอบ ${apiData.valuerRank}`,
        `สามารถประยุกต์ใช้ความรู้ในการทำงานจริง`,
        `พัฒนาทักษะการคิดวิเคราะห์และแก้ปัญหา`,
        `สร้างเครือข่ายและแลกเปลี่ยนประสบการณ์กับผู้เชี่ยวชาญ`
      ],
      whoShouldAttend: [
        `ผู้ที่สนใจสอบระดับ ${apiData.valuerRank}`,
        `ผู้ประกอบวิชาชีพที่ต้องการพัฒนาทักษะ`,
        `นักศึกษาและบัณฑิตจบใหม่`,
        `ผู้บริหารและเจ้าของธุรกิจ`
      ]
    };
  }

  /**
   * Get valuer rank badge class
   */
  getValuerRankBadgeClass(valuerRank: string): string {
    const rankMap: { [key: string]: string } = {
      'วิสามัญ': 'badge-danger',
      'สามัญ': 'badge-warning',
      'วุฒิ': 'badge-success',
      'วุฒิ+smart pv': 'badge-info'
    };
    return rankMap[valuerRank] || 'badge-secondary';
  }

  /**
   * Change active tab
   */
  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  /**
   * Check if tab is active
   */
  isActiveTab(tab: string): boolean {
    return this.activeTab === tab;
  }


  /**
   * Format date for display
   */
  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  }

  /**
   * Format price with currency
   */
  formatPrice(price: number): string {
    return new Intl.NumberFormat('th-TH', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price);
  }

  /**
   * Generate star rating array
   */
  getStarRating(rating: number): { full: number; half: boolean; empty: number } {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return {
      full: fullStars,
      half: hasHalfStar,
      empty: emptyStars
    };
  }

  /**
   * Get exam poster URL with fallback
   */
  getExamPosterUrl(): string {
    if (this.examData?.posterImageUrl) {
      return this.examData.posterImageUrl;
    }

    // Return SVG data URL for placeholder
    const svgContent = `
      <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#f59e0b;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#d97706;stop-opacity:1" />
          </linearGradient>
        </defs>
        <rect width="400" height="300" fill="url(#grad)" />
      </svg>
    `;

    // Use data URI with charset=utf-8 to support Thai characters
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgContent);
  }

  /**
   * Enroll in exam
   */
  enrollExam(): void {
    console.log('='.repeat(80));
    console.log('🔔 BUTTON CLICKED: ลงทะเบียนสอบ');
    console.log('='.repeat(80));
    console.log('📊 EXAM DATA (Full Object):', this.examData);
    console.log('📋 Exam ID:', this.examData?.id);
    console.log('📝 Exam Topic:', this.examData?.examTopic);
    console.log('⭐ Valuer Rank:', this.examData?.valuerRank);
    console.log('💰 Exam Fee:', this.examData?.examFee);
    console.log('📅 Exam Date:', this.examData?.examDate);
    console.log('⏰ Exam Time:', this.examData?.examTime);
    console.log('📍 Exam Location:', this.examData?.examLocation);
    console.log('🎯 CPD Hours Display:', this.examData?.cpdHoursDisplay);
    console.log('📊 Exam Status:', this.examData?.examStatus);
    console.log('🔢 Exam Status ID:', this.examData?.examStatusId);
    console.log('📝 Remarks:', this.examData?.remarks);
    console.log('👥 Enrolled Count:', this.examData?.enrolledCount);
    console.log('='.repeat(80));

    if (!this.examData?.id) {
      console.error('❌ No exam data or exam ID found!');
      alert('ไม่พบข้อมูลการสอบ');
      return;
    }

    console.log('🎓 Starting exam enrollment...');
    console.log('📋 Exam ID:', this.examData.id);

    // Get userId from localStorage (check both 'currentUser' and 'userData')
    let userDataStr = localStorage.getItem('currentUser');
    if (!userDataStr) {
      userDataStr = localStorage.getItem('userData');
    }

    console.log('🔍 Checking localStorage for user data...');
    console.log('📦 currentUser:', localStorage.getItem('currentUser'));
    console.log('📦 userData:', localStorage.getItem('userData'));

    if (!userDataStr) {
      console.error('❌ No user data found in localStorage');
      alert('กรุณาเข้าสู่ระบบก่อนลงทะเบียน');
      this.router.navigate(['/login']);
      return;
    }

    let userId: string;
    try {
      const userData = JSON.parse(userDataStr);
      console.log('👤 Parsed user data:', userData);

      // Try multiple possible field names for userId
      userId = userData.id || userData.userId || userData.user?.id;

      if (!userId) {
        console.error('❌ No userId found in userData:', userData);
        console.error('❌ Available keys:', Object.keys(userData));
        alert('ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่');
        this.router.navigate(['/login']);
        return;
      }

      console.log('✅ User ID found:', userId);
    } catch (error) {
      console.error('❌ Error parsing userData:', error);
      alert('ข้อมูลผู้ใช้ไม่ถูกต้อง กรุณาเข้าสู่ระบบใหม่');
      this.router.navigate(['/login']);
      return;
    }

    // Check if file is selected
    if (!this.selectedFile) {
      console.error('❌ No file selected');
      alert('กรุณาอัพโหลดหลักฐานการสมัครสอบก่อนลงทะเบียน');
      return;
    }

    console.log('📎 Selected file:', this.selectedFile.name, 'Size:', this.selectedFile.size);

    // Prepare enrollment data according to API specification
    const currentDate = new Date();
    
    // Create FormData for multipart/form-data upload
    const formData = new FormData();
    
    // Add all enrollment fields
    formData.append('userId', userId);
    formData.append('examId', this.examData.id);
    formData.append('hoursStudied', '0');
    formData.append('studyDate', this.examData.examDate || currentDate.toISOString().split('T')[0]);
    formData.append('year', currentDate.getFullYear().toString());
    formData.append('sessionTitle', this.examData.examTopic);
    formData.append('notes', `ลงทะเบียนสอบ: ${this.examData.examTopic}`);
    formData.append('isVerified', 'false');
    formData.append('paymentStatus', 'ยังไม่ได้ชำระเงิน');
    formData.append('enrolledAt', currentDate.toISOString());
    formData.append('progress', '0');
    formData.append('isCompleted', 'false');
    
    // Add the file (evidence document)
    formData.append('attachFile', this.selectedFile, this.selectedFile.name);

    console.log('📤 Enrollment data prepared with file:', this.selectedFile.name);

    // Confirm enrollment
    const confirmMessage = `ยืนยันการลงทะเบียนสอบ\n\n` +
      `การสอบ: ${this.examData.examTopic}\n` +
      `ระดับผู้ประเมิน: ${this.examData.valuerRank}\n` +
      `ค่าลงทะเบียน: ฿${this.formatPrice(this.examData.examFee)}\n` +
      `ไฟล์แนบ: ${this.selectedFile.name}\n\n` +
      `ต้องการดำเนินการต่อหรือไม่?`;

    if (!confirm(confirmMessage)) {
      console.log('❌ User cancelled enrollment');
      return;
    }

    // Send enrollment request with file
    this.isLoading = true;
    const apiUrl = `${environment.apiUrl}/exam-lists`;
    console.log('📡 Sending enrollment with file to:', apiUrl);

    this.http.post(apiUrl, formData).subscribe({
      next: (response) => {
        console.log('✅ Enrollment successful:', response);
        this.isLoading = false;

        alert('ลงทะเบียนสำเร็จ!\n\nคุณสามารถติดตามสถานะการสมัครสอบได้ที่หน้า Dashboard ของคุณ');

        // Navigate back to dashboard
        this.router.navigate(['/user/dashboard']);
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Enrollment error:', error);
        console.error('❌ Error status:', error.status);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error body:', error.error);

        this.isLoading = false;

        let errorMessage = 'ไม่สามารถลงทะเบียนได้ กรุณาลองใหม่อีกครั้ง';

        if (error.status === 400) {
          errorMessage = 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบและลองใหม่';
        } else if (error.status === 409) {
          errorMessage = 'คุณได้ลงทะเบียนสอบนี้แล้ว';
        } else if (error.status === 404) {
          errorMessage = 'ไม่พบข้อมูลการสอบหรือผู้ใช้';
        } else if (error.status === 0) {
          errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบว่า Backend ทำงานอยู่';
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }

        alert(errorMessage);
      }
    });
  }

  /**
   * Handle file selection
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      // Reset errors
      this.fileError = null;
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        this.fileError = 'กรุณาเลือกไฟล์ประเภท JPG, PNG หรือ PDF เท่านั้น';
        this.selectedFile = null;
        this.filePreviewUrl = null;
        return;
      }
      
      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        this.fileError = 'ขนาดไฟล์ต้องไม่เกิน 5MB';
        this.selectedFile = null;
        this.filePreviewUrl = null;
        return;
      }
      
      // Set selected file
      this.selectedFile = file;
      console.log('📎 File selected:', file.name, 'Size:', (file.size / 1024).toFixed(2), 'KB');
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
          this.filePreviewUrl = e.target?.result as string;
          this.cdr.detectChanges();
        };
        reader.readAsDataURL(file);
      } else {
        this.filePreviewUrl = null;
      }
    }
  }

  /**
   * Remove selected file
   */
  removeFile(): void {
    this.selectedFile = null;
    this.filePreviewUrl = null;
    this.fileError = null;
    
    // Reset file input
    const fileInput = document.getElementById('examEvidenceFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    
    console.log('🗑️ File removed');
  }

  /**
   * Check if enrollment button should be disabled
   */
  isEnrollDisabled(): boolean {
    return !this.selectedFile || this.isLoading;
  }

  /**
   * Go back to dashboard
   */
  goBack(): void {
    this.router.navigate(['/user/dashboard']);
  }
}
